// app.permissions / app.can() — the browser side of custom permissions.
//
// Resolves the session's JWT groups against the COMPILED permissions map
// (`.s3p/permissions.compiled.json`, member-readable — written by sync;
// custom ids + function names only, never storage globs). The resolution
// itself is `checkCompiledPermission` in policy-templates.ts, shared with
// the Lambda runtime's `ctx.caller.can()` so browser and server agree.
//
// TRUST: this is UI gating. The browser copy of a JWT group list can be
// tampered with like any client state — a custom permission is ENFORCED
// only where the runtime checks it (`ctx.caller.require`) or where IAM
// carries it (storage/function grants). Same contract as Auth0/Clerk
// client-side `has()`.

import {
  GetObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { paths } from "./paths.ts";
import {
  type CompiledPermissionsMap,
  checkCompiledPermission,
  resolvedCustomPermissions,
} from "./policy-templates.ts";

export interface PermissionsModule {
  /** Is the signed-in user allowed `id`? Custom ids (`"reports:approve"`)
   *  and function invokes (`"fn:generate-report"`). False when signed out.
   *  Fetches the compiled map once and caches it (60s TTL) — cheap enough
   *  to call per render. */
  can(id: string): Promise<boolean>;
  /** The session's resolved custom permission ids. */
  list(): Promise<string[]>;
  /** Drop the cache (e.g. right after a role assignment or sync). */
  refresh(): void;
}

const TTL_MS = 60_000;

export function createPermissionsModule(deps: {
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** Current session's JWT groups; empty when signed out. */
  groups: () => string[];
  isAuthenticated: () => boolean;
}): PermissionsModule {
  let cached: { map: CompiledPermissionsMap | null; at: number } | undefined;

  async function loadMap(): Promise<CompiledPermissionsMap | null> {
    if (cached && Date.now() - cached.at < TTL_MS) return cached.map;
    let map: CompiledPermissionsMap | null = null;
    try {
      const s3 = await deps.getClient();
      const res = await s3.send(
        new GetObjectCommand({ Bucket: deps.bucket, Key: paths().permissionsCompiled() }),
      );
      const body = await (
        res.Body as { transformToString: () => Promise<string> } | undefined
      )?.transformToString();
      if (body) map = JSON.parse(body) as CompiledPermissionsMap;
    } catch {
      // Missing key (project never synced under v2) or no read yet — the
      // checker treats a null map as "no custom permissions, floor-default
      // function access". Cached too: absence is an answer, not an error.
    }
    cached = { map, at: Date.now() };
    return map;
  }

  return {
    async can(id: string) {
      if (!deps.isAuthenticated()) return false;
      return checkCompiledPermission(await loadMap(), deps.groups(), id);
    },
    async list() {
      if (!deps.isAuthenticated()) return [];
      return resolvedCustomPermissions(await loadMap(), deps.groups());
    },
    refresh() {
      cached = undefined;
    },
  };
}
